import { Button } from '@synoptic/ui-kit/button/button.js';
import { FileInput } from '@synoptic/ui-kit/file-input/file-input.tsx';
import { Hint } from '@synoptic/ui-kit/hint/hint.tsx';
import { HelpCircleIcon } from '@synoptic/ui-kit/icons/react/help-circle.tsx';
import { ModalHeader, ModalTitle } from '@synoptic/ui-kit/modal/modal.tsx';
import { SegmentedSlider } from '@synoptic/ui-kit/slider/segmented-slider.tsx';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { LexicalEditor } from 'lexical';
import { ReactNode, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { StreamPostInput } from '@/__generated__/graphql.ts';
import { RegularPostInput } from '@/__generated__/schema-types.ts';
import { useUserUploads } from '../s3/user/use-uploads.ts';
import { waitForUploads } from '../s3/wait-for-uploads.ts';
import { TextEditor } from '../text-editor/text-editor.tsx';
import { stateToMarkdown } from '../text-editor/utils/state-to-markdown.ts';
import { stateToText } from '../text-editor/utils/state-to-text.ts';
import { UploadPreview } from '../upload-preview/upload-preview.tsx';
import {
  formGroup,
  formGroupLabel,
  formGroupOptionalLabel,
  formImgSection,
  hintIcon,
  newPostActions,
  newPostActionsRow,
  newPostActionsSubmitButton,
  newPostContent,
  newPostContentContainer,
  newPostForm,
} from './form.css.ts';

type FormInputs = {
  hasTextValue: boolean;
  reputationBet: number;
};

export type NewPostFormProps<
  T extends RegularPostInput | StreamPostInput = RegularPostInput,
> = {
  onSubmit: (input: T) => Promise<void>;
  label?: ReactNode;
  withReputationBet?: boolean;
  titleText?: string;
};

export function NewPostForm<
  T extends RegularPostInput | StreamPostInput = RegularPostInput,
>({
  onSubmit,
  label,
  withReputationBet = true,
  titleText = 'Create new post',
}: NewPostFormProps<T>) {
  const { handleSubmit, formState, control } = useForm<FormInputs>({
    defaultValues: { reputationBet: 0 },
  });
  const toast = useToast();
  const {
    uploadFileList,
    uploads,
    onRemove: onRemoveUpload,
  } = useUserUploads({ limit: 4, mode: 'append' });

  const [isDragOver, setIsDragOver] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);
  const editorRef = useRef<LexicalEditor | null>(null);

  const submit: SubmitHandler<FormInputs> = async (data) => {
    if (editorRef.current) {
      let media;
      try {
        media = await waitForUploads(uploads);
      } catch {
        toast.error({
          title: 'Some of your media failed to upload.',
        });
        return;
      }

      const editorState = editorRef.current.getEditorState();

      const markdown = stateToMarkdown(editorState).trim();

      return onSubmit({
        text: markdown,
        json: JSON.stringify(editorState.toJSON()),
        markdown,
        reputationBet: withReputationBet ? data.reputationBet : undefined,
        media,
      } as unknown as T);
    }
  };

  return (
    <form
      className={newPostForm}
      ref={formRef}
      // eslint-disable-next-line react-hooks/refs
      onSubmit={handleSubmit(submit)}
      data-drag-over={isDragOver ? '' : undefined}
      onKeyDownCapture={(e) => {
        if (formRef.current && e.key === 'Enter' && e.metaKey) {
          e.stopPropagation();

          formRef.current.requestSubmit();
        }
      }}
      onDragOver={(e) => {
        if (e.dataTransfer.items.length > 0) {
          e.preventDefault();
          setIsDragOver(true);
        }
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        if (e.dataTransfer.files.length > 0) {
          e.preventDefault();
          uploadFileList(e.dataTransfer.files);
          setIsDragOver(false);
        }
      }}
      onPaste={(e) => {
        if (e.clipboardData.files.length > 0) {
          e.preventDefault();
          uploadFileList(e.clipboardData.files);
        }
      }}
    >
      <ModalHeader>
        <ModalTitle>{titleText}</ModalTitle>
      </ModalHeader>
      <div className={newPostContentContainer}>
        <div className={newPostContent}>
          <Controller
            name="hasTextValue"
            control={control}
            rules={{
              validate: (v) => Boolean(v),
            }}
            render={({ field }) => (
              <TextEditor
                namespace="new-post"
                rich
                editorRef={(el) => {
                  field.ref(el);
                  editorRef.current = el;
                }}
                onBlur={field.onBlur}
                theme="textarea"
                autoFocus
                editable
                placeholder="What are your thoughts?"
                onChange={(state) => {
                  field.onChange(stateToText(state).trim().length > 0);
                }}
              />
            )}
          />

          {uploads.length > 0 ? (
            <div
              data-testid={'new-post-img-section'}
              className={formImgSection}
            >
              {uploads.map(({ key, file, progress, state }) => (
                <UploadPreview
                  key={key}
                  file={file}
                  state={state}
                  progress={progress}
                  onRemove={() => onRemoveUpload(key)}
                  compact
                />
              ))}
            </div>
          ) : null}

          {withReputationBet ? (
            <div className={formGroup}>
              <div className={formGroupLabel}>
                Choose reputation bet
                <Hint
                  description={
                    'Reputation bet sets how much reputation user is willing to risk on this post.'
                  }
                >
                  <HelpCircleIcon className={hintIcon} />
                </Hint>
              </div>
              <div className={formGroupOptionalLabel}>Optional field</div>
              <Controller
                name="reputationBet"
                control={control}
                render={({ field }) => (
                  <SegmentedSlider
                    min={0}
                    defaultValue={[field.value]}
                    onValueCommit={([v]) => field.onChange(v)}
                    disabled={field.disabled}
                    step={0.1}
                  />
                )}
              />
            </div>
          ) : null}

          {label ? <div>{label}</div> : null}
        </div>
      </div>

      <div className={newPostActions}>
        <div className={newPostActionsRow}>
          <FileInput
            onChange={(e) => {
              if (e.target.files) {
                uploadFileList(e.target.files);
                e.target.value = '';
              }
            }}
            accept="image/*,video/*"
            multiple
            rounded={false}
            size="medium"
          >
            Attach media
          </FileInput>
        </div>

        <Button
          disabled={!formState.isValid}
          type="submit"
          size={'medium'}
          className={newPostActionsSubmitButton}
          loading={formState.isSubmitting}
        >
          Post
        </Button>
      </div>
    </form>
  );
}
